import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { switchMap } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { throwError } from 'rxjs';
import { Title } from '@angular/platform-browser';

import { environment } from '../../environments/environment';
import { IRoughcheck, IAssessment, ISection, IRisk, IReward } from '../roughcheck';
import { MessageService } from '../message.service';
import { RoughcheckService } from '../roughcheck.service';
import { LoggerService } from '../logger.service';

@Component({
  selector: 'app-roughcheck',
  templateUrl: './roughcheck.component.html',
})
export class RoughcheckComponent implements OnInit {
  @Input() rref: string;

  roughcheck: IRoughcheck = {
    rref: '',
    title: '',
    image: '',
    image_alt: '',
    category: '',
    riskprofile: '',
    rewardprofile: '',
    show_risk_reward: 1,
    description: '',
    slug: '',
    status: 0,
    status_email: 0,
    feedback: 0,
    sponsor: '',
    website: ''
  };
  assessment: IAssessment = {};
  risks: IRisk[];
  rewards: IReward[];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: RoughcheckService,
    private location: Location,
    private message: MessageService,
    private logger: LoggerService,
    private titleService: Title,
  ) { }

  /**
   * Initialise this Roughcheck component and load if an rref is available
   * or go to the start page
   */
  ngOnInit(): void {
    this.titleService.setTitle( 'Roughcheck' );
    this.service.setRefFromUrl(this.route.snapshot);
    if (this.service.getRRef()) {
      this.getRoughcheck();
    }
  }

  /**
   * Load component
   */
  private getRoughcheck(): void {
    this.service.getHttpRoughcheck()
      .subscribe(
        (resp) => this.handleSuccess(resp),
        (error) => this.service.handleError(error)
      );
  }

  private gotoForward(): void {
    const gotopage = this.service.getGoToPage();
    this.service.setGoToPage('');
    if (gotopage) {
      if (! this.service.getRRef()) {
        this.router.navigate(['/start']);
      } else if (gotopage === 'assessment') {
        if (this.service.getARef()) {
          this.router.navigate(['/a', this.service.getRRef(), this.service.getARef(), 'assessment']);
        } else {
          this.router.navigate(['/a', this.service.getRRef(), 'assessment']);
        }
      } else if (gotopage === 'sections') {
        if (this.service.getARef()) {
          this.router.navigate(['/a', this.service.getRRef(), this.service.getARef(), 'sections']);
        } else {
          this.router.navigate(['/a', this.service.getRRef(), 'sections']);
        }
      } else if (gotopage === 'report') {
        if (this.service.getARef()) {
          this.router.navigate(['/a', this.service.getRRef(), this.service.getARef(), 'report']);
        }
      } else if (gotopage === 'action') {
        if (this.service.getARef()) {
          this.router.navigate(['/a', this.service.getRRef(), this.service.getARef(), 'action']);
        }
      } else if (gotopage === 'help') {
        if (this.service.getARef()) {
          this.router.navigate(['/a', this.service.getRRef(), this.service.getARef(), 'help']);
        } else {
          this.router.navigate(['/a', this.service.getRRef(), 'help']);
        }
      } else {
        console.log('RoughcheckComponent handleSuccess Unable to go to: ' + gotopage);
      }
    }
  }

  /**
   * Populate Roughcheck and user assessment
   */
  private handleSuccess(resp: HttpResponse<any>) {
    this.roughcheck = {
      rref: resp.body.roughcheck.rref,
      title: resp.body.roughcheck.title,
      image: resp.body.roughcheck.image,
      image_alt: resp.body.roughcheck.image_alt,
      category: resp.body.roughcheck.category,
      riskprofile: resp.body.roughcheck.riskprofile,
      rewardprofile: resp.body.roughcheck.rewardprofile,
      show_risk_reward: resp.body.roughcheck.show_risk_reward,
      description: resp.body.roughcheck.description,
      slug: resp.body.roughcheck.slug,
      status: resp.body.roughcheck.status,
      status_email: resp.body.roughcheck.status_email,
      feedback: resp.body.roughcheck.feedback,
      sponsor: resp.body.roughcheck.sponsor,
      website: resp.body.roughcheck.website
    };
    this.service.setRoughcheck(this.roughcheck);
    this.assessment = {
      aref: resp.body.aref,
      uref: resp.body.uref,
      title: resp.body.title,
      email: resp.body.email,
      description: resp.body.description,
      date_completed: resp.body.date_completed,
      status: resp.body.status
    };
    this.service.setAssessment(this.assessment);
    this.titleService.setTitle( this.roughcheck.title );
    this.getRiskRewards();
  }

  private getRiskRewards(): void {
    this.service.getHttpRisks()
      .subscribe(
        (resp) => this.handleRisksSuccess(resp),
        (error) => this.service.handleError(error)
      );
    this.service.getHttpRewards()
      .subscribe(
        (resp) => this.handleRewardsSuccess(resp),
        (error) => this.service.handleError(error)
      );
    this.service.getHttpSections()
      .subscribe(
        (resp) => this.handleSectionsSuccess(resp),
        (error) => this.service.handleError(error)
      );
  }

  /**
   * Populate Risks
   */
  private handleRisksSuccess(resp: HttpResponse<any>) {
    const risks: IRisk[] = resp.body;
    this.service.setRisks(risks);
  }
  /**
   * Populate Rewards
   */
  private handleRewardsSuccess(resp: HttpResponse<any>) {
    const rewards: IReward[] = resp.body;
    this.service.setRewards(rewards);
  }
  /**
   * Populate Sections then go to the first one
   */
  private handleSectionsSuccess(resp: HttpResponse<any>) {
    const sections: ISection[] = resp.body;
    this.service.setSections(sections);
    if (this.service.getSection()) {
      // Using an existing section, so reset objects to update object identity
      const id = this.service.getSection().id;
      for (const section of sections) {
        if (id === section.id) {
          this.service.setSection(section);
          break;
        }
      }
    } else {
      this.service.setSection(sections[0]);
    }
    this.gotoForward();
  }

  /**
   * Change the Roughcheck
   */
  clickChange() {
    this.router.navigateByUrl('/start');
  }

  /**
   * Start or continue the Roughcheck assessment by
   * going to the assessment
   */
  clickNext() {
    if ( this.assessment.aref ) {
      this.router.navigate(['/a', this.roughcheck.rref, this.assessment.aref, 'assessment']);
    } else {
      this.router.navigate(['/a', this.roughcheck.rref, 'assessment']);
    }
  }

  goBack(): void {
    this.location.back();
  }

}
